import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { Panel } from "./components/Panel";
import { fetchCards, sendTranscript, streamChat } from "./api";
import { configureEvents, getSessionId, track } from "./session";
import { getUserLocation, isNearMeQuery, type UserLocation } from "./geo";
import type {
  ChatHistoryMessage,
  ChatMessage,
  ClientCard,
  ThemeColors,
} from "./types";

const DEFAULT_BRAND_NAME = "Assistant";
const DEFAULT_WELCOME =
  "Hi! Ask me anything about our products, services, or process.";

// Most recent messages forwarded to the API per request. The full visible
// transcript stays in the panel; this only bounds what we send so a long
// session never trips the server's per-request cap. Older turns are dropped
// silently ("forgotten") rather than surfacing an error. Keep in sync with
// the `messages` array max in api/src/routes/chat.ts.
const MAX_HISTORY = 100;

export interface AppOptions {
  clientId: string;
  apiUrl: string;
  // Optional now — if any are undefined, App will fetch /v1/config/:clientId
  // and use the returned values. Explicit init options (admin test page,
  // or rare snippet overrides) take precedence over the fetched values.
  brandName?: string;
  welcomeMessage?: string;
  starterPrompts?: string[];
  // Provided by mount.ts. Re-applies theme CSS custom properties on the
  // shadow host when /v1/config returns the admin-saved colors, which win
  // over the install snippet's data attributes.
  applyTheme?: (colors: ThemeColors) => void;
}

interface ConfigResponse {
  brandName?: string;
  welcomeMessage?: string | null;
  starterPrompts?: string[];
  emailCcAvailable?: boolean;
  gaPushEnabled?: boolean;
  theme?: ThemeColors;
}

interface Props {
  options: AppOptions;
}

export function App({ options }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // Stable per-visit correlation id: every chat turn, transcript, and
  // conversion event from this visit carries it so the API can tie
  // conversions back to the conversation that drove them.
  const [sessionId] = useState(() => getSessionId());
  // Fire chat_started exactly once per widget mount (≈ once per visit).
  const chatStartedRef = useRef(false);
  // Visitor coordinates, cached after the first granted "near me"
  // question so only that first question waits on the browser prompt.
  const userLocationRef = useRef<UserLocation | null>(null);

  // brand/welcome/starters live in state so the API fetch can fill them in
  // after first paint. Initial values prefer the explicit init option, then
  // fall back to a placeholder default. The first frame shows the default
  // briefly; once the fetch resolves (typically <100ms), the client-specific
  // values land.
  const [brandName, setBrandName] = useState(
    options.brandName ?? DEFAULT_BRAND_NAME,
  );
  const [welcomeMessage, setWelcomeMessage] = useState(
    options.welcomeMessage ?? DEFAULT_WELCOME,
  );
  const [starterPrompts, setStarterPrompts] = useState<string[]>(
    options.starterPrompts ?? [],
  );
  const [emailCcAvailable, setEmailCcAvailable] = useState(false);
  const [gaPush, setGaPush] = useState(false);
  const [cards, setCards] = useState<ClientCard[]>([]);

  // Register this widget's API context (incl. the GA-push flag) so deep click
  // handlers and the host bridge can fire conversion events without
  // prop-drilling. Re-runs once /v1/config resolves gaPush.
  useEffect(() => {
    configureEvents({
      apiUrl: options.apiUrl,
      clientId: options.clientId,
      gaPush,
    });
  }, [options.apiUrl, options.clientId, gaPush]);

  useEffect(() => {
    let cancelled = false;
    fetchCards(options.apiUrl, options.clientId).then((list) => {
      if (!cancelled) setCards(list);
    });
    return () => {
      cancelled = true;
    };
  }, [options.apiUrl, options.clientId]);

  useEffect(() => {
    // Only fetch when at least one content field wasn't supplied by init.
    // The admin test page passes everything imperatively from the DB and
    // shouldn't trigger a redundant network round-trip.
    const allProvided =
      options.brandName !== undefined &&
      options.welcomeMessage !== undefined &&
      options.starterPrompts !== undefined;
    if (allProvided) return;

    let cancelled = false;
    const url = `${options.apiUrl}/v1/config/${encodeURIComponent(options.clientId)}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg: ConfigResponse | null) => {
        if (cancelled || !cfg) return;
        if (options.brandName === undefined) {
          // Honor a blank admin Display name: an empty brand means "omit the
          // brand from the widget" (the terms line + transcript copy), not
          // "fall back to the default" — same contract as welcomeMessage.
          setBrandName(cfg.brandName ?? "");
        }
        if (options.welcomeMessage === undefined) {
          // Honor a blank admin field: a null/empty welcome message means
          // "show no welcome line", not "fall back to the default".
          setWelcomeMessage(cfg.welcomeMessage ?? "");
        }
        if (options.starterPrompts === undefined && cfg.starterPrompts) {
          setStarterPrompts(cfg.starterPrompts);
        }
        // emailCcAvailable is always taken from /v1/config — it's not an
        // init option (no upstream caller knows about it). Defaults to
        // false in initial state, so the CC checkbox just doesn't render
        // until /v1/config confirms it.
        if (cfg.emailCcAvailable !== undefined) {
          setEmailCcAvailable(cfg.emailCcAvailable);
        }
        if (cfg.gaPushEnabled !== undefined) {
          setGaPush(cfg.gaPushEnabled);
        }
        // Admin-saved theme wins over snippet data attributes. applyTheme
        // skips null/empty values, so unset DB columns leave the attribute
        // or default styling in place.
        if (cfg.theme && options.applyTheme) {
          options.applyTheme(cfg.theme);
        }
      })
      .catch((err) => {
        // Swallow — widget keeps the placeholder defaults; user can still
        // ask questions, just without per-client branding/welcome.
        console.warn("AIWidget: /v1/config fetch failed", err);
      });

    return () => {
      cancelled = true;
    };
    // Only re-run if the identity of the widget changes. Capturing options.*
    // values without re-running on every render is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.apiUrl, options.clientId]);

  const onSendTranscript = useCallback(
    async (email: string, ccTeam: boolean, honeypot: string) => {
      const result = await sendTranscript({
        apiUrl: options.apiUrl,
        clientId: options.clientId,
        sessionId,
        visitorEmail: email,
        ccTeam,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        honeypot,
      });
      // Emailing oneself the transcript is a lead-capture conversion.
      if (result.ok) track("transcript_email", undefined, { ccTeam });
      return result;
    },
    [options.apiUrl, options.clientId, sessionId, messages],
  );

  const send = useCallback(
    async (text: string) => {
      if (!chatStartedRef.current) {
        chatStartedRef.current = true;
        track("chat_started");
      }
      // Detect before any await so the geolocation permission prompt
      // still counts as user-gesture-driven on strict mobile browsers.
      const wantsLocation = isNearMeQuery(text);
      const userMsg: ChatMessage = { role: "user", content: text };
      const assistantMsg: ChatMessage = {
        role: "assistant",
        content: "",
        isStreaming: true,
        sources: [],
      };

      // Build the history we'll send to the API: existing messages plus
      // this new user turn. The streaming assistant placeholder is local
      // UI state only; the server will produce the assistant response.
      let history: ChatHistoryMessage[] = [];
      setMessages((prev) => {
        const full = [...prev, userMsg].map((m) => ({
          role: m.role,
          content: m.content,
        }));
        history = trimHistory(full, MAX_HISTORY);
        return [...prev, userMsg, assistantMsg];
      });
      setIsStreaming(true);

      // "Near me" questions trigger the browser location prompt right
      // here, after the typing placeholder is visible. Denial, timeout,
      // or an unsupported browser just means no coordinates: the request
      // proceeds and the assistant answers generally.
      if (wantsLocation && !userLocationRef.current) {
        userLocationRef.current = await getUserLocation();
      }

      try {
        await streamChat(
          {
            apiUrl: options.apiUrl,
            clientId: options.clientId,
            sessionId,
            // Once granted, keep sending the (rounded) coords for the rest
            // of the session so follow-ups like "how about plumbers?" can
            // still rank by distance. The server ignores coords unless the
            // query itself shows firm-seeking or location intent.
            messages: history,
            userLocation: userLocationRef.current ?? undefined,
          },
          {
            onMeta: (sources, cards, reps) => {
              setMessages((prev) =>
                updateLastAssistant(prev, { sources, cards, reps }),
              );
            },
            onText: (delta) => {
              setMessages((prev) =>
                updateLastAssistant(prev, (m) => ({
                  content: m.content + delta,
                })),
              );
            },
            onDone: () => {
              setMessages((prev) =>
                updateLastAssistant(prev, { isStreaming: false }),
              );
            },
            onError: (msg) => {
              // Keep the technical detail in the console for debugging, but
              // never show raw HTTP/JSON to the visitor. If we'd already
              // streamed partial text, leave it in place.
              console.error("AIWidget: chat request failed —", msg);
              setMessages((prev) =>
                updateLastAssistant(prev, (m) => ({
                  content:
                    m.content ||
                    "Sorry — I ran into a problem answering that. Please try again in a moment.",
                  isStreaming: false,
                })),
              );
            },
          },
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [options.apiUrl, options.clientId, sessionId],
  );

  return (
    <Panel
      clientId={options.clientId}
      brandName={brandName}
      welcomeMessage={welcomeMessage}
      starterPrompts={starterPrompts}
      messages={messages}
      isStreaming={isStreaming}
      onSubmit={send}
      emailCcAvailable={emailCcAvailable}
      onSendTranscript={onSendTranscript}
      cards={cards}
    />
  );
}

// Keep only the most recent `max` messages, then drop any leading
// assistant turns so the forwarded window starts on a user message
// (providers expect the first message to be the user's, and the server
// retrieves sources against the latest user turn regardless). Returns the
// input unchanged when it's already within bounds.
function trimHistory(
  messages: ChatHistoryMessage[],
  max: number,
): ChatHistoryMessage[] {
  let out =
    messages.length > max ? messages.slice(messages.length - max) : messages;
  while (out.length > 1 && out[0]!.role !== "user") {
    out = out.slice(1);
  }
  return out;
}

type Updater =
  | Partial<ChatMessage>
  | ((current: ChatMessage) => Partial<ChatMessage>);

function updateLastAssistant(
  messages: ChatMessage[],
  update: Updater,
): ChatMessage[] {
  if (messages.length === 0) return messages;
  const last = messages[messages.length - 1]!;
  if (last.role !== "assistant") return messages;
  const patch = typeof update === "function" ? update(last) : update;
  const next = messages.slice();
  next[next.length - 1] = { ...last, ...patch };
  return next;
}
